/**
 * LeftShield Landing Page
 *
 * Public marketing page ported from the original LandingPageV2 (shadcn/Tailwind)
 * to Fluent UI v9 with makeStyles + tokens.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Button,
  Badge,
  Text,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Input,
  Label,
  Textarea,
  shorthands,
  mergeClasses,
} from '@fluentui/react-components';
import {
  ShieldCheckmarkRegular,
  DatabaseRegular,
  BrainCircuitRegular,
  EyeRegular,
  BotRegular,
  PeopleRegular,
  LockClosedRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  ArrowRightRegular,
  BranchRegular,
  CloudRegular,
  DocumentRegular,
  WarningRegular,
  NumberSymbolRegular,
  ServerRegular,
  PulseRegular,
  ChevronRightRegular,
  ArrowDownRegular,
} from '@fluentui/react-icons';
import { solutionTokens, semanticColors } from '../theme/solution-theme';

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  // Layout
  page: {
    width: '100%',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
  },

  // Nav
  nav: {
    position: 'sticky',
    top: '0',
    zIndex: 50,
    // Deliberate fixed translucent-black backdrop for the glass nav effect —
    // no brand token represents raw black-at-alpha; left as-is (no visual change).
    backgroundColor: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(12px)',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  navInner: {
    maxWidth: '1280px',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  navBrand: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    textDecoration: 'none',
    color: tokens.colorNeutralForeground1,
  },
  navLogo: {
    height: '56px',
    width: 'auto',
  },
  navBrandText: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: 700,
  },
  navLinks: {
    display: 'none',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXXL,
    '@media (min-width: 768px)': {
      display: 'flex',
    },
  },
  navAnchor: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    textDecoration: 'none',
    transitionProperty: 'color',
    transitionDuration: '150ms',
    ':hover': {
      color: tokens.colorNeutralForeground1,
      textDecoration: 'none',
    },
  },

  // Sections
  section: {
    // 96px = spacingVerticalXXXL (32px) × 3 — beyond the fixed token scale, composed from it.
    paddingTop: `calc(${tokens.spacingVerticalXXXL} * 3)`,
    paddingBottom: `calc(${tokens.spacingVerticalXXXL} * 3)`,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
  },
  sectionMuted: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  sectionInner: {
    maxWidth: '1280px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  sectionInnerNarrow: {
    maxWidth: '1024px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  sectionHeader: {
    textAlign: 'center' as const,
    // 64px = spacingVerticalXXXL (32px) × 2.
    marginBottom: `calc(${tokens.spacingVerticalXXXL} * 2)`,
  },
  sectionTitle: {
    // 36px / 48px: marketing display scale outside Fluent's fontSize token
    // set (10/12/14/16/20/24/28/32/40/68) — no exact token, left as-is.
    fontSize: '36px',
    fontWeight: 700,
    marginBottom: tokens.spacingVerticalL,
    lineHeight: '1.2',
    '@media (min-width: 768px)': {
      fontSize: '48px',
    },
  },
  sectionSubtitle: {
    // 18px: no exact Fluent fontSize token (16 or 20 are the neighbours) — left as-is.
    fontSize: '18px',
    color: tokens.colorNeutralForeground3,
    maxWidth: '640px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },

  // Hero
  hero: {
    position: 'relative',
    overflow: 'hidden',
    paddingTop: `calc(${tokens.spacingVerticalXXXL} * 3)`,
    paddingBottom: `calc(${tokens.spacingVerticalXXXL} * 3)`,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    '@media (min-width: 1024px)': {
      // 128px = spacingVerticalXXXL (32px) × 4.
      paddingTop: `calc(${tokens.spacingVerticalXXXL} * 4)`,
      paddingBottom: `calc(${tokens.spacingVerticalXXXL} * 4)`,
    },
  },
  heroGlow: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '800px',
    height: '800px',
    background: `radial-gradient(circle, ${tokens.colorBrandBackground}15 0%, transparent 70%)`,
    borderRadius: '50%',
    pointerEvents: 'none',
  },
  heroContent: {
    position: 'relative',
    maxWidth: '1280px',
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center' as const,
  },
  heroTitle: {
    // 48/72/84px: marketing display scale outside Fluent's fontSize token
    // set — no exact token, left as-is.
    fontSize: '48px',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    marginBottom: tokens.spacingVerticalXXL,
    lineHeight: '1.1',
    '@media (min-width: 768px)': {
      fontSize: '72px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '84px',
    },
  },
  heroGradientText: {
    backgroundImage: `linear-gradient(90deg, ${tokens.colorBrandForeground1}, ${solutionTokens.brandSecondary})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  heroSubtitle: {
    fontSize: tokens.fontSizeBase500,
    lineHeight: '1.2',
    color: tokens.colorNeutralForeground3,
    maxWidth: '768px',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: tokens.spacingVerticalXXXL,
    '@media (min-width: 768px)': {
      fontSize: tokens.fontSizeBase600,
    },
  },
  heroDesc: {
    // 18px: no exact Fluent fontSize token — left as-is.
    fontSize: '18px',
    lineHeight: '1.2',
    color: tokens.colorNeutralForeground3,
    maxWidth: '896px',
    marginLeft: 'auto',
    marginRight: 'auto',
    // 48px = spacingVerticalXXXL (32px) + spacingVerticalL (16px).
    marginBottom: `calc(${tokens.spacingVerticalXXXL} + ${tokens.spacingVerticalL})`,
  },
  heroCtas: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalL,
    marginBottom: `calc(${tokens.spacingVerticalXXXL} * 2)`,
    '@media (min-width: 640px)': {
      flexDirection: 'row',
    },
  },
  heroTagline: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '9999px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: tokens.fontSizeBase300,
  },

  // Architecture
  archSection: {
    // 64px = spacingVerticalXXXL (32px) × 2.
    paddingTop: `calc(${tokens.spacingVerticalXXXL} * 2)`,
    paddingBottom: `calc(${tokens.spacingVerticalXXXL} * 2)`,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  archBox: {
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '12px',
    ...shorthands.padding('24px'),
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: tokens.spacingVerticalXXL,
  },
  archBoxLabel: {
    textAlign: 'center' as const,
    marginBottom: tokens.spacingVerticalL,
    // 11px: no exact Fluent fontSize token (10/12 are the neighbours) — left as-is.
    fontSize: '11px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
    color: tokens.colorNeutralForeground3,
    fontWeight: 600,
  },
  archChips: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalM,
  },
  archChip: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: tokens.fontSizeBase300,
  },
  archArrow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalL,
    marginBottom: tokens.spacingVerticalL,
    // 11px: no exact Fluent fontSize token — left as-is.
    fontSize: '11px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
  },
  archOsBox: {
    // #3B82F6 (blue) is not in brand-config.json's palette — no token represents
    // it, so it is left as a raw literal to avoid changing the gradient's appearance.
    background: `linear-gradient(90deg, ${tokens.colorBrandBackground}18, ${solutionTokens.brandSecondary}18, #3B82F618)`,
    borderRadius: '12px',
    ...shorthands.padding('32px'),
    border: `1px solid ${tokens.colorBrandStroke1}40`,
    marginBottom: tokens.spacingVerticalXXL,
  },
  archOsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: tokens.spacingHorizontalM,
    '@media (min-width: 768px)': {
      gridTemplateColumns: '1fr 1fr 1fr',
    },
  },
  archOsItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    ...shorthands.padding('12px'),
    backgroundColor: `${tokens.colorNeutralBackground1}CC`,
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: tokens.fontSizeBase300,
    fontWeight: 500,
  },

  // Grid layouts
  grid2: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: tokens.spacingHorizontalXXL,
    '@media (min-width: 768px)': {
      gridTemplateColumns: '1fr 1fr',
    },
  },
  grid3: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: tokens.spacingHorizontalXXL,
    '@media (min-width: 768px)': {
      gridTemplateColumns: '1fr 1fr',
    },
    '@media (min-width: 1024px)': {
      gridTemplateColumns: '1fr 1fr 1fr',
    },
  },
  grid2col: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalL,
  },

  // Capability cards
  capCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '12px',
    ...shorthands.padding('24px'),
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    transitionProperty: 'border-color',
    transitionDuration: '200ms',
    ':hover': {
      borderTopColor: tokens.colorBrandStroke1,
      borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1,
      borderLeftColor: tokens.colorBrandStroke1,
    },
  },
  capIconBox: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: tokens.spacingVerticalL,
  },
  capTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: 600,
    marginBottom: tokens.spacingVerticalS,
  },
  capDesc: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
    lineHeight: '1.6',
  },

  // Problem section
  problemGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    // 64px = spacingHorizontalXXXL (32px) × 2.
    gap: `calc(${tokens.spacingHorizontalXXXL} * 2)`,
    alignItems: 'center',
    '@media (min-width: 1024px)': {
      gridTemplateColumns: '1fr 1fr',
    },
  },
  problemItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalL,
  },
  problemResult: {
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '16px',
    ...shorthands.padding('32px'),
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  problemResultItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
  },

  // Solution section
  solutionSection: {
    paddingTop: `calc(${tokens.spacingVerticalXXXL} * 3)`,
    paddingBottom: `calc(${tokens.spacingVerticalXXXL} * 3)`,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    background: `linear-gradient(180deg, ${tokens.colorBrandBackground}08, ${tokens.colorNeutralBackground1})`,
  },
  solutionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
    ...shorthands.padding('16px'),
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontWeight: 500,
  },
  solutionTruth: {
    textAlign: 'center' as const,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '16px',
    ...shorthands.padding('32px'),
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    marginTop: `calc(${tokens.spacingVerticalXXXL} * 2)`,
    fontSize: tokens.fontSizeBase500,
    fontWeight: 500,
  },

  // How It Works
  step: {
    display: 'flex',
    gap: tokens.spacingHorizontalXXL,
    ...shorthands.padding('24px'),
    borderRadius: '16px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    marginBottom: tokens.spacingVerticalXXXL,
  },
  stepHighlight: {
    backgroundColor: `${tokens.colorBrandBackground}08`,
    border: `1px solid ${tokens.colorBrandStroke1}40`,
  },
  stepNumber: {
    flexShrink: 0,
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // 18px: no exact Fluent fontSize token — left as-is.
    fontSize: '18px',
    fontWeight: 700,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
  },
  stepNumberHighlight: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  stepContent: {
    flex: '1',
  },
  stepTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalS,
  },
  stepTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: 600,
  },
  stepDesc: {
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalL,
    lineHeight: '1.6',
  },
  stepBadges: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },

  // Comparison
  compCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '12px',
    ...shorthands.padding('24px'),
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    transitionProperty: 'box-shadow',
    transitionDuration: '200ms',
    ':hover': {
      boxShadow: tokens.shadow8,
    },
  },
  compCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM,
  },
  compHighlightCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding('32px'),
    background: `linear-gradient(135deg, ${tokens.colorBrandBackground}18, ${solutionTokens.brandSecondary}18)`,
    borderRadius: '12px',
    border: `1px solid ${tokens.colorBrandStroke1}40`,
    textAlign: 'center' as const,
    fontWeight: 500,
  },

  // Security
  securityItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    ...shorthands.padding('16px'),
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: tokens.fontSizeBase300,
  },

  // CTA
  ctaSection: {
    paddingTop: `calc(${tokens.spacingVerticalXXXL} * 3)`,
    paddingBottom: `calc(${tokens.spacingVerticalXXXL} * 3)`,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    background: `linear-gradient(180deg, ${tokens.colorNeutralBackground1}, ${tokens.colorBrandBackground}12)`,
    textAlign: 'center' as const,
  },
  ctaCtas: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalXXXL,
    '@media (min-width: 640px)': {
      flexDirection: 'row',
    },
  },

  // Footer
  footer: {
    // 48px = spacingVerticalXXXL (32px) + spacingVerticalXXL (16... 24px, see below).
    paddingTop: `calc(${tokens.spacingVerticalXXXL} + ${tokens.spacingVerticalL})`,
    paddingBottom: `calc(${tokens.spacingVerticalXXXL} + ${tokens.spacingVerticalL})`,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  footerInner: {
    maxWidth: '1280px',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalXXL,
    '@media (min-width: 768px)': {
      flexDirection: 'row',
    },
  },
  footerBrand: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  footerLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXL,
    fontSize: tokens.fontSizeBase300,
  },
  footerLink: {
    color: tokens.colorNeutralForeground3,
    textDecoration: 'none',
    transitionProperty: 'color',
    transitionDuration: '150ms',
    ':hover': {
      color: tokens.colorNeutralForeground1,
      textDecoration: 'none',
    },
  },

  // Dialog form
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXS,
    marginBottom: tokens.spacingVerticalL,
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalL,
  },

  // Utility
  mb4: { marginBottom: tokens.spacingVerticalL },
  mb6: { marginBottom: tokens.spacingVerticalXXL },
  textCenter: { textAlign: 'center' as const },
  iconPrimary: { color: tokens.colorBrandForeground1 },
  iconMuted: { color: tokens.colorNeutralForeground3 },
});

// =============================================================================
// COMPONENT
// =============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://localhost:7001/api';

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

export function LandingPage() {
  const styles = useStyles();
  const [contactDialogOpen, setContactDialogOpen] = useState(false);

  // Form state
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [submitError, setSubmitError] = useState('');

  const resetForm = () => {
    setEmail('');
    setCompany('');
    setMessage('');
    setWebsite('');
    setSubmitState('idle');
    setSubmitError('');
  };

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitState('submitting');
    setSubmitError('');

    try {
      const res = await fetch(`${API_BASE_URL}/home/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          company,
          message: message || null,
          source: 'landing-page-contact',
          website: website || null,
        }),
      });

      if (res.status === 429) {
        const data = await res.json().catch(() => ({}));
        setSubmitState('error');
        setSubmitError(data.message || 'Too many requests. Please try again later.');
        return;
      }

      if (!res.ok) {
        setSubmitState('error');
        setSubmitError('Something went wrong. Please try again.');
        return;
      }

      setSubmitState('success');
      setTimeout(() => {
        setContactDialogOpen(false);
        resetForm();
      }, 2000);
    } catch {
      setSubmitState('error');
      setSubmitError('Unable to reach the server. Please try again.');
    }
  };

  return (
    <div className={styles.page}>
      {/* ================================================================ */}
      {/* NAVIGATION */}
      {/* ================================================================ */}
      <nav className={styles.nav}>
        <div className={styles.navInner}>
          <a href="#" className={styles.navBrand}>
            <img src="/logo-compact.png" alt="LeftShield" className={styles.navLogo} />
          </a>

          <div className={styles.navLinks}>
            <a href="#capabilities" className={styles.navAnchor}>Capabilities</a>
            <a href="#how-it-works" className={styles.navAnchor}>How It Works</a>
            <a href="#security" className={styles.navAnchor}>Security</a>

            <Link to="/login" style={{ textDecoration: 'none' }}>
              <Button appearance="primary" size="small">Sign In</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* ================================================================ */}
      {/* HERO */}
      {/* ================================================================ */}
      <section className={styles.hero}>
        <div className={styles.heroGlow} />

        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>
            Agentic SDLC
            <br />
            <span className={styles.heroGradientText}>Control Plane</span>
          </h1>

          <p className={styles.heroSubtitle}>
            Runtime enforcement infrastructure that powers compliance frameworks. 
          </p>

          {/* <p className={styles.heroDesc}>
            LeftShield is an Agentic SDLC Control Plane that maps your entire software delivery lifecycle,
            detects risks, evolves controls, enforces guardrails, and proves compliance — continuously.
          </p> */}

          <div className={styles.heroCtas}>
            <Link to="/login" style={{ textDecoration: 'none' }}>
              <Button
                appearance="primary"
                size="large"
                icon={<ArrowRightRegular />}
                iconPosition="after"
              >
                Get Started
              </Button>
            </Link>
            <Button
              appearance="outline"
              size="large"
              icon={<ChevronRightRegular />}
              iconPosition="after"
              onClick={() => setContactDialogOpen(true)}
            >
              Talk to the Team
            </Button>
          </div>

          <div className={styles.heroTagline}>
            <PulseRegular style={{ fontSize: tokens.fontSizeBase500, color: tokens.colorBrandForeground1 }} />
            <span>
              Built for regulated engineering teams. LeftShield executes controls across the SDLC and generates cryptographically verifiable evidence automatically.
            </span>
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* ARCHITECTURE */}
      {/* ================================================================ */}
      <section className={styles.archSection}>
        <div className={styles.sectionInnerNarrow}>
          <div className={styles.sectionHeader}>
            <div className={styles.mb4}>
              <Badge appearance="outline">Architecture</Badge>
            </div>
            <h2 className={styles.sectionTitle} style={{ fontSize: tokens.fontSizeHero800 }}>
              The LeftShield Control Plane
            </h2>
          </div>

          {/* Engineering Systems */}
          <div className={styles.archBox}>
            <div className={styles.archBoxLabel}>Engineering Systems</div>
            <div className={styles.archChips}>
              {['GitHub', 'GitLab', 'CI/CD', 'AWS', 'IAM', 'Jira', 'IaC', 'Repos'].map((label) => (
                <div key={label} className={styles.archChip}>
                  <ArchIcon label={label} />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.archArrow}>
            <ArrowDownRegular style={{ fontSize: tokens.fontSizeBase500 }} />
            <span>Live Ingestion</span>
            <ArrowDownRegular style={{ fontSize: tokens.fontSizeBase500 }} />
          </div>

          {/* OS Layer */}
          <div className={styles.archOsBox}>
            <div style={{ textAlign: 'center', marginBottom: tokens.spacingVerticalXXL }}>
              <Badge appearance="filled" color="brand">LeftShield OS Layer</Badge>
            </div>
            <div className={styles.archOsGrid}>
              {[
                { Icon: PulseRegular, label: 'Temporal Compliance Graph (EKG)', color: tokens.colorBrandForeground1 },
                { Icon: NumberSymbolRegular, label: 'Evidence Chain Engine', color: solutionTokens.brandSecondary },
                // #3B82F6 / #F59E0B: not in brand-config.json's palette — no token represents
                // them, so they are left as raw literals to avoid changing these icon colours.
                { Icon: BrainCircuitRegular, label: 'Constitution Engine (AI)', color: '#3B82F6' },
                { Icon: PulseRegular, label: 'Risk Intelligence Brain', color: '#F59E0B' },
                { Icon: BotRegular, label: 'Agentic Guardrails', color: semanticColors.success },
                { Icon: EyeRegular, label: 'Assurance Engine', color: semanticColors.error },
              ].map((item) => (
                <div key={item.label} className={styles.archOsItem}>
                  <item.Icon style={{ fontSize: tokens.fontSizeBase500, color: item.color }} />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.archArrow}>
            <ArrowDownRegular style={{ fontSize: tokens.fontSizeBase500 }} />
            <span>Unified Output</span>
            <ArrowDownRegular style={{ fontSize: tokens.fontSizeBase500 }} />
          </div>

          {/* Visible Experiences */}
          <div className={styles.archBox}>
            <div className={styles.archBoxLabel}>Visible Experiences</div>
            <div className={styles.archChips}>
              {['Digital Twin', 'Assurance Dashboard', 'Auditor Portal', 'Governance Activity', 'Policy Controls'].map((label) => (
                <div key={label} className={styles.archChip}>
                  <span style={{ fontWeight: 500 }}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* CAPABILITIES */}
      {/* ================================================================ */}
      <section id="capabilities" className={mergeClasses(styles.section, styles.sectionMuted)}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <div className={styles.mb4}>
              <Badge appearance="outline">Capabilities</Badge>
            </div>
            <h2 className={styles.sectionTitle}>Now delivering</h2>
            <p className={styles.sectionSubtitle}>
              Six core capabilities that transform how you approach compliance
            </p>
          </div>

          <div className={styles.grid3}>
            <CapabilityCard
              Icon={DatabaseRegular}
              color={tokens.colorBrandForeground1}
              bgColor={`${tokens.colorBrandBackground}18`}
              title="Digital Compliance Twin"
              description="A live, queryable map of every service, control, risk signal, evidence chain, and incident in your SDLC."
            />
            <CapabilityCard
              Icon={NumberSymbolRegular}
              color={solutionTokens.brandSecondary}
              bgColor={`${solutionTokens.brandSecondary}18`}
              title="Temporal Evidence Integrity"
              description="Immutable, hash-linked evidence with time-versioned lineage. Prove compliance without screenshots or spreadsheets."
            />
            {/* #3B82F6 (blue) is not in brand-config.json's palette — no token represents it,
                so it is left as a raw literal to avoid changing this card's appearance. */}
            <CapabilityCard
              Icon={BrainCircuitRegular}
              color="#3B82F6"
              bgColor="#3B82F618"
              title="AI Constitution Engine"
              description="Controls evolve themselves based on drift, incidents, code behavior, and risk patterns — and come to you for approval."
            />
            <CapabilityCard
              Icon={EyeRegular}
              color={semanticColors.success}
              bgColor={`${semanticColors.success}18`}
              title="Explainable Assurance"
              description="Run on-demand or continuous risk assessments with full reasoning, policy coverage, evidence analysis, and counterfactual simulations."
            />
            {/* #F59E0B (amber) is not in brand-config.json's palette — no token represents it,
                so it is left as a raw literal to avoid changing this card's appearance. */}
            <CapabilityCard
              Icon={BotRegular}
              color="#F59E0B"
              bgColor="#F59E0B18"
              title="Agentic Guardrails"
              description="Autonomous governance that comments, enforces, or proposes fixes across repositories, pipelines, and cloud environments."
            />
            <CapabilityCard
              Icon={PeopleRegular}
              color={semanticColors.error}
              bgColor={`${semanticColors.error}18`}
              title="Auditor & Regulator Portal"
              description="Give auditors controlled read-only access to real-time controls, evidence, and assurance data — no manual evidence collection ever again."
            />
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* PROBLEM */}
      {/* ================================================================ */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.problemGrid}>
            <div>
              <div className={styles.mb4}>
                <Badge appearance="filled" color="danger">The Problem</Badge>
              </div>
              <h2 className={styles.sectionTitle} style={{ textAlign: 'left' }}>
                Security and compliance are broken.
              </h2>

              {[
                'DevSecOps tools scan but don\'t understand compliance.',
                'GRC tools produce documents but don\'t understand SDLC reality.',
                'Policies exist on paper but are rarely enforced.',
                'Evidence is screenshots, not proof.',
                'Audits are annual fire drills.',
                'Engineering slows down to meet compliance requirements.',
              ].map((item) => (
                <div key={item} className={styles.problemItem}>
                  <DismissCircleRegular style={{ fontSize: tokens.fontSizeBase500, color: tokens.colorPaletteRedForeground1, flexShrink: 0, marginTop: tokens.spacingVerticalXXS }} />
                  <Text style={{ color: tokens.colorNeutralForeground3 }}>{item}</Text>
                </div>
              ))}
            </div>

            <div className={styles.problemResult}>
              <h3 style={{ fontSize: tokens.fontSizeBase500, fontWeight: 600, marginBottom: tokens.spacingVerticalXXL, color: tokens.colorPaletteRedForeground1 }}>
                The result:
              </h3>
              <div className={styles.grid2col}>
                {[
                  'No single source of truth',
                  'No real-time insight',
                  'No provable assurance',
                  'No automation',
                  'No intelligence',
                  'No continuous compliance',
                ].map((item) => (
                  <div key={item} className={styles.problemResultItem}>
                    {/* #F59E0B (amber) is not in brand-config.json's palette — left as a raw literal. */}
                    <WarningRegular style={{ fontSize: tokens.fontSizeBase400, color: '#F59E0B' }} />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* SOLUTION */}
      {/* ================================================================ */}
      <section className={styles.solutionSection}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <div className={styles.mb4}>
              <Badge appearance="filled" color="brand">The Solution</Badge>
            </div>
            <h2 className={styles.sectionTitle}>The LeftShield Breakthrough</h2>
            <p className={styles.sectionSubtitle} style={{ fontSize: tokens.fontSizeBase500 }}>
              Connect your SDLC to begin automatic evidence collection, policy enforcement, and real-time assurance.
            </p>
          </div>

          <div className={styles.grid3}>
            {[
              { Icon: PulseRegular, label: 'Temporal knowledge graph', color: tokens.colorBrandForeground1 },
              { Icon: PulseRegular, label: 'Continuous SDLC signal ingestion', color: solutionTokens.brandSecondary },
              // #3B82F6 / #F59E0B: not in brand-config.json's palette — left as raw literals.
              { Icon: BotRegular, label: 'Agentic governance', color: '#3B82F6' },
              { Icon: BrainCircuitRegular, label: 'Automatic control evolution', color: semanticColors.success },
              { Icon: NumberSymbolRegular, label: 'Immutable evidence chains', color: '#F59E0B' },
              { Icon: DatabaseRegular, label: 'Full digital twin', color: semanticColors.error },
            ].map((item) => (
              <div key={item.label} className={styles.solutionItem}>
                <item.Icon style={{ fontSize: tokens.fontSizeBase600, color: item.color }} />
                <span>{item.label}</span>
              </div>
            ))}
          </div>

          <div className={styles.solutionTruth}>
            <p>
              You don't get dashboards.
              <br />
              <span style={{ color: tokens.colorBrandForeground1 }}>You get truth</span> — versioned, provable, and always up to date.
            </p>
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* HOW IT WORKS */}
      {/* ================================================================ */}
      <section id="how-it-works" className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <div className={styles.mb4}>
              <Badge appearance="outline">How It Works</Badge>
            </div>
            <h2 className={styles.sectionTitle}>Six steps to continuous compliance</h2>
          </div>

          <HowItWorksStep
            styles={styles}
            number={1}
            title="Connect your SDLC"
            description="GitHub, CI/CD, cloud accounts, ticketing/incident systems, and audit frameworks. LeftShield begins building your real-time compliance model instantly."
            Icon={BranchRegular}
            items={['GitHub', 'CI/CD Pipelines', 'Cloud Accounts', 'Incident Systems']}
          />
          <HowItWorksStep
            styles={styles}
            number={2}
            title="Build Your Digital Compliance Twin"
            description="LeftShield maps services, controls, policies, deployments, cloud drift, code behaviors, pipelines, evidence, risk signals, and incidents into one continuous temporal graph."
            Icon={DatabaseRegular}
            items={['No manual configuration', 'No separate modules', 'No integration pain']}
            highlight
          />
          <HowItWorksStep
            styles={styles}
            number={3}
            title="Governance Becomes Autonomous"
            description="The Constitution Engine analyzes violations, drift, engineering patterns, incidents, risk scoring, and evidence gaps — and proposes improved controls or updated policies."
            Icon={BrainCircuitRegular}
            items={['PRs', 'Pipelines', 'Cloud changes', 'IaC', 'Identity configurations']}
          />
          <HowItWorksStep
            styles={styles}
            number={4}
            title="Evidence is Recorded Automatically"
            description="Every piece of evidence is cryptographically hashed, linked to the previous record, stored with temporal context, and attached to controls automatically."
            Icon={NumberSymbolRegular}
            items={['No screenshots', 'No manual uploads', 'No auditor surprises']}
            highlight
          />
          <HowItWorksStep
            styles={styles}
            number={5}
            title="Assurance Becomes Continuous"
            description="Run assessments at any time to see real-time risk scores, control coverage, evidence quality, drift and incident impacts, and counterfactual simulations."
            Icon={EyeRegular}
            items={['Real-time risk scores', 'Control coverage', 'Evidence quality', 'Counterfactual simulations']}
          />
          <HowItWorksStep
            styles={styles}
            number={6}
            title="Auditors Get a Portal"
            description="Auditors receive a read-only, real-time view of evidence, controls, assessment history, Digital Twin snapshots, graph relationships, and temporal lineage."
            Icon={PeopleRegular}
            items={['No more audit prep', 'Compliance is continuous']}
            highlight
          />
        </div>
      </section>

      {/* ================================================================ */}
      {/* COMPARISON */}
      {/* ================================================================ */}
      <section className={mergeClasses(styles.section, styles.sectionMuted)}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <div className={styles.mb4}>
              <Badge appearance="outline">Why LeftShield</Badge>
            </div>
            <h2 className={styles.sectionTitle}>Why Teams Choose LeftShield</h2>
          </div>

          <div className={styles.grid3}>
            <ComparisonCard
              styles={styles}
              Icon={BranchRegular}
              title="Dev-first like Snyk & GitHub"
              description="But understands policies, controls, evidence, and risk — not just code issues."
            />
            <ComparisonCard
              styles={styles}
              Icon={ShieldCheckmarkRegular}
              title="Assurance-grade like Drata & Vanta"
              description="But powered by real technical evidence, not screenshots or attestations."
            />
            <ComparisonCard
              styles={styles}
              Icon={DocumentRegular}
              title="Governance like OPA/Sentinel"
              description="But autonomous, self-evolving, and aware of engineering behavior."
            />
            <ComparisonCard
              styles={styles}
              Icon={EyeRegular}
              title="Visibility like JupiterOne & Wiz"
              description="But unified with controls, policies, incidents, and compliance reasoning."
            />
            <ComparisonCard
              styles={styles}
              Icon={BrainCircuitRegular}
              title="Agentic & self-improving"
              description="LeftShield learns from every event and continuously strengthens governance."
            />
            <div className={styles.compHighlightCard}>
              <p>
                No other platform combines all of these dimensions into{' '}
                <span style={{ color: tokens.colorBrandForeground1 }}>one system</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* SECURITY & TRUST */}
      {/* ================================================================ */}
      <section id="security" className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <div className={styles.mb4}>
              <Badge appearance="outline" icon={<LockClosedRegular />}>Security &amp; Trust</Badge>
            </div>
            <h2 className={styles.sectionTitle}>Architected for high-trust environments</h2>
          </div>

          <div className={styles.grid3}>
            {[
              'Passwords hashed with bcrypt',
              'TLS enforced end-to-end',
              'Strict tenant isolation',
              'Least-privileged GitHub and cloud tokens',
              'Immutable evidence chain with hash verification',
              'Temporal lineage for all graph entities',
              'Full data deletion support',
              'Regulator-grade audit API',
              'Optional high-assurance deployment modes',
            ].map((item) => (
              <div key={item} className={styles.securityItem}>
                <CheckmarkCircleRegular style={{ fontSize: tokens.fontSizeBase500, color: semanticColors.success, flexShrink: 0 }} />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* CTA */}
      {/* ================================================================ */}
      <section className={styles.ctaSection}>
        <div style={{ maxWidth: '896px', marginLeft: 'auto', marginRight: 'auto' }}>
          <h2 className={styles.sectionTitle} style={{ marginBottom: tokens.spacingVerticalXXL }}>
            Get Started with LeftShield
          </h2>
          <p style={{ fontSize: tokens.fontSizeBase500, color: tokens.colorNeutralForeground3, marginBottom: tokens.spacingVerticalXXXL }}>
            Get migration support, dedicated onboarding, and help connecting your SDLC.
          </p>

          <div className={styles.ctaCtas}>
            <Link to="/login" style={{ textDecoration: 'none' }}>
              <Button
                appearance="primary"
                size="large"
                icon={<ArrowRightRegular />}
                iconPosition="after"
              >
                Get Started
              </Button>
            </Link>
            <Button
              appearance="outline"
              size="large"
              icon={<ChevronRightRegular />}
              iconPosition="after"
              onClick={() => setContactDialogOpen(true)}
            >
              Talk to the Team
            </Button>
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* FOOTER */}
      {/* ================================================================ */}
      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.footerBrand}>
            <img src="/logo.png" alt="LeftShield" style={{ height: '24px' }} />
            <span style={{ fontWeight: 600 }}>LeftShield</span>
            <span style={{ fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3, marginLeft: tokens.spacingHorizontalS }}>
              &copy; {new Date().getFullYear()} All rights reserved
            </span>
          </div>

          <div className={styles.footerLinks}>
            <a href="#" className={styles.footerLink}>Privacy</a>
            <a href="#" className={styles.footerLink}>Terms</a>
            <Link to="/login" className={styles.footerLink}>Login</Link>
          </div>
        </div>
      </footer>

      {/* ================================================================ */}
      {/* DIALOGS */}
      {/* ================================================================ */}

      {/* Contact Dialog */}
      <Dialog open={contactDialogOpen} onOpenChange={(_e, data) => {
        setContactDialogOpen(data.open);
        if (!data.open) resetForm();
      }}>
        <DialogSurface>
          <form onSubmit={handleContactSubmit}>
            <DialogBody>
              <DialogTitle>Contact Our Team</DialogTitle>
              <DialogContent>
                {submitState === 'success' ? (
                  <div style={{ textAlign: 'center', padding: `${tokens.spacingVerticalXXL} 0` }}>
                    {/* 48px icon size / 18px "Thank you!" text: decorative dialog display
                        sizes outside Fluent's fontSize token set — left as-is. */}
                    <CheckmarkCircleRegular style={{ fontSize: '48px', color: semanticColors.success, marginBottom: tokens.spacingVerticalM }} />
                    <Text block style={{ fontSize: '18px', fontWeight: 600 }}>Thank you!</Text>
                    <Text block style={{ color: tokens.colorNeutralForeground3 }}>We'll be in touch soon.</Text>
                  </div>
                ) : (
                  <>
                    <Text block style={{ color: tokens.colorNeutralForeground3, marginBottom: tokens.spacingVerticalL }}>
                      Have questions? Want a personalized demo? We'd love to hear from you.
                    </Text>
                    <div className={styles.formRow}>
                      <div className={styles.formField}>
                        <Label htmlFor="contact-email" required>Email</Label>
                        <Input
                          id="contact-email"
                          type="email"
                          placeholder="you@company.com"
                          required
                          value={email}
                          onChange={(_e, data) => setEmail(data.value)}
                          disabled={submitState === 'submitting'}
                        />
                      </div>
                      <div className={styles.formField}>
                        <Label htmlFor="contact-company" required>Company</Label>
                        <Input
                          id="contact-company"
                          placeholder="Your company"
                          required
                          value={company}
                          onChange={(_e, data) => setCompany(data.value)}
                          disabled={submitState === 'submitting'}
                        />
                      </div>
                    </div>
                    <div className={styles.formField}>
                      <Label htmlFor="contact-message">Message</Label>
                      <Textarea
                        id="contact-message"
                        placeholder="Tell us about your use case or questions..."
                        rows={4}
                        value={message}
                        onChange={(_e, data) => setMessage(data.value)}
                        disabled={submitState === 'submitting'}
                      />
                    </div>
                    {/* Honeypot — hidden from real users. -9999px is an off-screen
                        positioning technique, not a design spacing value — left as-is. */}
                    <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                      <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                      />
                    </div>
                    {submitState === 'error' && submitError && (
                      <Text block style={{ color: tokens.colorPaletteRedForeground1, marginBottom: tokens.spacingVerticalS, fontSize: tokens.fontSizeBase300 }}>
                        {submitError}
                      </Text>
                    )}
                  </>
                )}
              </DialogContent>
              {submitState !== 'success' && (
                <DialogActions>
                  <Button
                    appearance="secondary"
                    onClick={() => { setContactDialogOpen(false); resetForm(); }}
                    disabled={submitState === 'submitting'}
                  >
                    Cancel
                  </Button>
                  <Button
                    appearance="primary"
                    type="submit"
                    disabled={submitState === 'submitting'}
                  >
                    {submitState === 'submitting' ? 'Sending...' : 'Send Message'}
                  </Button>
                </DialogActions>
              )}
            </DialogBody>
          </form>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function ArchIcon({ label }: { label: string }) {
  const iconStyle = { fontSize: tokens.fontSizeBase400, color: tokens.colorNeutralForeground3 };
  switch (label) {
    case 'GitHub':
    case 'GitLab':
    case 'Repos':
      return <BranchRegular style={iconStyle} />;
    case 'CI/CD':
      return <CloudRegular style={iconStyle} />;
    case 'AWS':
      return <ServerRegular style={iconStyle} />;
    case 'IAM':
      return <LockClosedRegular style={iconStyle} />;
    case 'Jira':
      return <DocumentRegular style={iconStyle} />;
    case 'IaC':
      return <DocumentRegular style={iconStyle} />;
    default:
      return <ServerRegular style={iconStyle} />;
  }
}

function CapabilityCard({
  Icon,
  color,
  bgColor,
  title,
  description,
}: {
  Icon: React.ElementType;
  color: string;
  bgColor: string;
  title: string;
  description: string;
}) {
  const styles = useStyles();
  return (
    <div className={styles.capCard}>
      <div className={styles.capIconBox} style={{ backgroundColor: bgColor }}>
        <Icon style={{ fontSize: tokens.fontSizeBase600, color }} />
      </div>
      <h3 className={styles.capTitle}>{title}</h3>
      <p className={styles.capDesc}>{description}</p>
    </div>
  );
}

function HowItWorksStep({
  styles,
  number,
  title,
  description,
  Icon,
  items,
  highlight,
}: {
  styles: ReturnType<typeof useStyles>;
  number: number;
  title: string;
  description: string;
  Icon: React.ElementType;
  items: string[];
  highlight?: boolean;
}) {
  return (
    <div className={mergeClasses(styles.step, highlight ? styles.stepHighlight : undefined)}>
      <div className={mergeClasses(styles.stepNumber, highlight ? styles.stepNumberHighlight : undefined)}>
        {number}
      </div>
      <div className={styles.stepContent}>
        <div className={styles.stepTitleRow}>
          <Icon style={{ fontSize: tokens.fontSizeBase500, color: highlight ? tokens.colorBrandForeground1 : tokens.colorNeutralForeground3 }} />
          <h3 className={styles.stepTitle}>{title}</h3>
        </div>
        <p className={styles.stepDesc}>{description}</p>
        <div className={styles.stepBadges}>
          {items.map((item) => (
            <Badge key={item} appearance="outline" size="medium">{item}</Badge>
          ))}
        </div>
      </div>
    </div>
  );
}

function ComparisonCard({
  styles,
  Icon,
  title,
  description,
}: {
  styles: ReturnType<typeof useStyles>;
  Icon: React.ElementType;
  title: string;
  description: string;
}) {
  return (
    <div className={styles.compCard}>
      <div className={styles.compCardHeader}>
        <Icon style={{ fontSize: tokens.fontSizeBase500, color: tokens.colorBrandForeground1 }} />
        <span style={{ fontWeight: 600 }}>{title}</span>
      </div>
      <Text style={{ fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3 }}>{description}</Text>
    </div>
  );
}

export default LandingPage;
